import React from "react"
import { graphql } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"

import { IntlProvider } from "react-intl"

import smoothscroll from "smoothscroll-polyfill"

import "focus-visible"

import PageHeader from "../components/page-header"
import LinkToTop from "../components/link-to-top"

import { PageFieldsFragment, LanguagesDataFragment } from "../../graphql-types"
import { NavigationDataItem } from "../utils/extract-navigation"

import extractMessages from "../utils/extract-messages"
import MainHero from "./main-hero"
import CookieConsent from "./cookie-consent"

// Run smoothscroll polyfill once.
// This adds support for `behavior: "smooth"` in `scrollTo()` in browsers where
// it's missing.
if (typeof window !== "undefined") {
  smoothscroll.polyfill()
  document.addEventListener("click", e => {
    const target = e.target as HTMLElement
    const href = target.getAttribute("href")
    if (target.tagName !== "A" || href === null) {
      return
    }
    const hrefParts = href.split("#")
    if (
      hrefParts.length !== 2 ||
      (hrefParts[0] && !pathsMatch(hrefParts[0], location.pathname))
    ) {
      return
    }
    const targetElement = document.getElementById(hrefParts[1])
    if (targetElement) {
      scrollTo({
        top: targetElement.offsetTop,
        behavior: "smooth",
      })
    }
  })
}
const pathsMatch = (a: string, b: string): boolean => {
  return removeTrailingSlash(a) === removeTrailingSlash(b)
}
const removeTrailingSlash = (path: string): string => {
  return path.substr(-1) === "/" ? path.slice(0, -1) : path
}

export const query = graphql`
  fragment PageFields on DatoCmsPage {
    slug
    title
    sectionTitle
    headerImage {
      url_s: url(imgixParams: { w: "800", fm: "jpg" })
      url_m: url(imgixParams: { w: "1200", fm: "jpg" })
      url_l: url(imgixParams: { w: "1600", fm: "jpg" })
      url_xl: url(imgixParams: { w: "2400", fm: "jpg" })
      width
      height
      blurhash
      focalPoint {
        x
        y
      }
    }
    headerVideo {
      video {
        high: mp4Url(res: high)
        medium: mp4Url(res: medium)
        low: mp4Url(res: low)
        streamingUrl
      }
    }
    useMainHero
    titleImage {
      url
    }
    preambleNode {
      childMarkdownRemark {
        html
      }
    }
    ctas {
      id
      text
      url
      isTransparent
      openInNewTab
      hasColor
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
  }
`

interface PageProps extends PageFieldsFragment {
  translations: LanguagesDataFragment
  navigationData: NavigationDataItem[]
  navigationSecondaryData: NavigationDataItem[]
  headerVariant?: "hasTitle" | "dark"
  metaDescription?: string
}

// Note: Keep these in sync with typography.scss.
const preloadFonts = [
  ["NorthvoltGritPro-Regular.woff2", "woff2"],
  ["NorthvoltGritPro-Medium.woff2", "woff2"],
  ["NorthvoltGritPro-SemiBold.woff2", "woff2"],
  ["NorthvoltGritPro-Bold.woff2", "woff2"],
]

const Page: React.FC<PageProps> = props => {
  return (
    <>
      <HelmetDatoCms seo={props.seoMetaTags}>
        {preloadFonts.map(([filename, format], i) => (
          <link
            key={i}
            rel="preload"
            as="font"
            href={`/fonts/${filename}`}
            type={`font/${format}`}
            crossOrigin="anonymous"
          />
        ))}
        {/* Work around missing description in seoMetaTags. */}
        {props.metaDescription && (
          <meta name="description" content={props.metaDescription} />
        )}
        {props.metaDescription && (
          <meta name="og:description" content={props.metaDescription} />
        )}
        {props.metaDescription && (
          <meta name="twitter:description" content={props.metaDescription} />
        )}
        <meta
          name="google-site-verification"
          content="jOXxu-nsBdFjbOw46TecJk4skzzG5-JrUcyyfmBDIrE"
        />
      </HelmetDatoCms>
      <IntlProvider
        messages={extractMessages(props.translations, "en")}
        locale="en"
        defaultLocale="en"
      >
        <LinkToTop></LinkToTop>
        {props.useMainHero && (
          <MainHero
            title={props.title}
            navigationData={props.navigationData}
            navigationSecondaryData={props.navigationSecondaryData}
            headerImage={props.headerImage.url_xl}
            headerVideo={props.headerVideo && props.headerVideo.video}
            titleImage={props.titleImage && props.titleImage.url}
            preamble={
              props.preambleNode && props.preambleNode.childMarkdownRemark.html
            }
            ctas={props.ctas}
            variant={props.headerVariant}
          />
        )}
        {!props.useMainHero && (
          <PageHeader
            title={props.title}
            sectionTitle={props.sectionTitle}
            navigationData={props.navigationData}
            navigationSecondaryData={props.navigationSecondaryData}
            headerImage={props.headerImage}
            headerVideo={props.headerVideo && props.headerVideo.video}
            variant={props.headerVariant}
          />
        )}
        {props.children}
        <CookieConsent />
      </IntlProvider>
    </>
  )
}

export default Page
